
import React, { useState, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useProjects } from '../hooks/useProjects';
import { useUsers } from '../hooks/useUsers';
import { useExpenses } from '../hooks/useExpenses';
import { Project, User, ExpenseEntry, MainCostCategory } from '../types';
import { COST_CATEGORIES } from '../constants';

type Status = 'idle' | 'parsing' | 'preview' | 'uploading' | 'done' | 'error';

// Normalization function to handle accents, case, spacing, and special characters.
const normalizeString = (str: string): string => {
    if (!str) return '';
    return str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Remove accents
        .toLowerCase()
        .replace(/[^a-z0-9]/g, ''); // Remove all non-alphanumeric characters
};

const parseBrazilianNumber = (str: string | undefined): number => {
    if (!str || str.trim() === '') return NaN;

    let cleaned = str.toString().trim().replace(/R\$\s?/g, ''); 

    // Handle Brazilian format (e.g., "1.500,50")
    if (cleaned.includes(',')) {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } 
    
    const number = parseFloat(cleaned);
    return isNaN(number) ? NaN : number;
};


export const ImportacaoPage: React.FC = () => {
    const { projects } = useProjects();
    const { users } = useUsers();
    const { saveExpense } = useExpenses();
    
    const [file, setFile] = useState<File | null>(null);
    const [status, setStatus] = useState<Status>('idle');
    const [processedData, setProcessedData] = useState<Partial<ExpenseEntry>[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
            setStatus('idle');
            setProcessedData([]);
            setErrors([]);
            setWarnings([]);
        }
    };
    
    const parseAndMapData = async () => {
        if (!file) return;
        setStatus('parsing');

        const normalizedFilename = normalizeString(file.name);
        let directEntryCategoryHint: MainCostCategory | null = null;
        if (normalizedFilename.includes('inss')) {
            directEntryCategoryHint = MainCostCategory.TRIBUTOS_INSS;
        } else if (normalizedFilename.includes('tributos')) {
            directEntryCategoryHint = MainCostCategory.TRIBUTOS;
        } else if (normalizedFilename.includes('despesasadm') || normalizedFilename.includes('despesasadministrativas')) {
            directEntryCategoryHint = MainCostCategory.DESPESAS_ADM;
        }

        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim() !== '');
        const header = lines[0].trim().split(',').map(h => h.trim());
        const rows = lines.slice(1);

        const newProcessedData: Partial<ExpenseEntry>[] = [];
        const newErrors: string[] = [];
        const newWarnings: string[] = [];
        
        // Mappers
        const projectMap = new Map(projects.map(p => [p.name, p.id]));
        const monthNameToNumber: { [key: string]: string } = {
            'janeiro': '01', 'fevereiro': '02', 'março': '03', 'abril': '04', 'maio': '05', 'junho': '06',
            'julho': '07', 'agosto': '08', 'setembro': '09', 'outubro': '10', 'novembro': '11', 'dezembro': '12'
        };

        const getMonthYear = (dateStr: string, monthName: string): string => {
            const year = new Date(dateStr).getFullYear();
            const monthNum = monthNameToNumber[monthName?.toLowerCase()];
            if (year && monthNum) {
                return `${year}-${monthNum}`;
            }
            const dateFromData = new Date(dateStr);
            if (!isNaN(dateFromData.getTime())) {
                return dateFromData.toISOString().substring(0, 7);
            }
            return new Date().toISOString().substring(0, 7);
        }

        const subCategoryColName = header.find(h => normalizeString(h) === 'entradacio' || normalizeString(h) === 'entradacdo');
        const isDirectEntry = header.includes('ValorSomado');

        for (let i = 0; i < rows.length; i++) {
            const line = rows[i].trim();
            if (!line) continue;
            
            const values = line.split(',');
            const rowData: { [key: string]: string } = header.reduce((obj, h, index) => {
                obj[h] = values[index] ? values[index].trim() : '';
                return obj;
            }, {} as { [key: string]: string });

            const entry: Partial<ExpenseEntry> = {};

            try {
                const obraName = rowData.Obra;
                let projectId: string | undefined = projectMap.get(obraName);
                
                if (!projectId) {
                    const possibleProjects = projects.filter(p => p.name.startsWith(obraName));
                    if (possibleProjects.length === 1) {
                        projectId = possibleProjects[0].id;
                    } else if (possibleProjects.length > 1) {
                         throw new Error(`Obra "${obraName}" é ambígua. Encontrados: ${possibleProjects.map(p => p.name).join(', ')}`);
                    }
                }

                if (!projectId) {
                    throw new Error(`Obra "${obraName}" não encontrada no banco de dados.`);
                }

                entry.projectId = projectId;
                entry.date = new Date(rowData.Data).toISOString();
                entry.observation = rowData.Observacao;

                if (subCategoryColName) {
                    const subCatNameFromCsv = rowData[subCategoryColName];
                    if (!subCatNameFromCsv) throw new Error(`Coluna de subcategoria (${subCategoryColName}) está vazia.`);
                    
                    let mainCatId: MainCostCategory | undefined;
                    if (normalizeString(subCategoryColName) === 'entradacdo') {
                        mainCatId = MainCostCategory.CDO;
                    } else if (normalizeString(subCategoryColName) === 'entradacio') {
                        mainCatId = MainCostCategory.CIO;
                    }

                    if (!mainCatId) {
                        throw new Error('Não foi possível determinar a categoria principal (CDO/CIO) pelo cabeçalho do arquivo.');
                    }
                    
                    const mainCategoryDetails = COST_CATEGORIES.find(c => c.id === mainCatId);
                    if (!mainCategoryDetails) {
                        throw new Error(`Detalhes da categoria principal ${mainCatId} não encontrados.`);
                    }

                    const normalizedSubCatNameFromCsv = normalizeString(subCatNameFromCsv);

                    // First, try for an exact match after normalization
                    let subCatInfo = mainCategoryDetails.subItems.find(si => normalizeString(si.name) === normalizedSubCatNameFromCsv);

                    // If no exact match, try a "starts with" approach for partial matches
                    if (!subCatInfo) {
                        const possibleMatches = mainCategoryDetails.subItems.filter(si => normalizeString(si.name).startsWith(normalizedSubCatNameFromCsv));
                        if (possibleMatches.length === 1) {
                            subCatInfo = possibleMatches[0];
                        } else if (possibleMatches.length > 1) {
                            // Ambiguous match, throw an error
                            throw new Error(`Subcategoria "${subCatNameFromCsv}" é ambígua. Corresponde a: ${possibleMatches.map(p => p.name).join(', ')}`);
                        }
                    }
                    
                    if (!subCatInfo) {
                       throw new Error(`Subcategoria ${mainCategoryDetails.name.split(' - ')[0]} "${subCatNameFromCsv}" inválida.`);
                    }

                    entry.mainCategory = mainCatId;
                    entry.subCategoryItemId = subCatInfo.id;
                    entry.subCategoryItemName = subCatInfo.name; // Use the official name from constants

                    entry.description = rowData.Descricao;
                    entry.supplier = rowData.Fornecedor;
                    entry.invoice = rowData.NotaFiscal;
                    entry.month = getMonthYear(rowData.Data, rowData.Mes);

                    let amount: number = NaN;
                    const valorStr = rowData.Valor || rowData.ValorMes;
                    
                    if (valorStr && valorStr.trim() !== '') {
                        amount = parseBrazilianNumber(valorStr);
                    } else {
                        const quantidadeStr = rowData.Quantidade;
                        const valorUnitarioStr = rowData.ValorUnitario;
                         if (quantidadeStr && valorUnitarioStr && quantidadeStr.trim() !== '' && valorUnitarioStr.trim() !== '') {
                            const quantidade = parseBrazilianNumber(quantidadeStr);
                            const valorUnitario = parseBrazilianNumber(valorUnitarioStr);
                            if (!isNaN(quantidade) && !isNaN(valorUnitario)) {
                                amount = quantidade * valorUnitario;
                            }
                        }
                    }
                    
                    if (isNaN(amount)) {
                        newWarnings.push(`Linha ${i + 2}: Valor monetário inválido. Verifique as colunas 'Valor', 'Quantidade' e 'ValorUnitario'. A linha será ignorada.`);
                        continue;
                    }
                    entry.amount = amount;
                    
                    const quantidade = parseBrazilianNumber(rowData.Quantidade);
                    const valorUnitario = parseBrazilianNumber(rowData.ValorUnitario);

                    entry.quantity = !isNaN(quantidade) ? quantidade : undefined;
                    entry.unitPrice = !isNaN(valorUnitario) ? valorUnitario : undefined;
                    entry.unit = rowData.Unidade as any;
                    entry.contract = rowData.PedidoContrato;
                    entry.contact = rowData.Contato;
                    entry.licensePlate = rowData.Placa;

                } else if (isDirectEntry) {
                    const nameColumnKey = Object.keys(rowData).find(key => normalizeString(key) === 'name' || normalizeString(key) === 'nome' || normalizeString(key) === 'descricao');
                    const nameColumnValue = nameColumnKey ? rowData[nameColumnKey] : '';
                    
                    let mainCat: MainCostCategory;
                    const normalizedName = normalizeString(nameColumnValue);

                    if (directEntryCategoryHint) {
                        mainCat = directEntryCategoryHint;
                    } else {
                        if (normalizedName.includes('inss')) {
                            mainCat = MainCostCategory.TRIBUTOS_INSS;
                        } else if (normalizedName.includes('tributos')) {
                            mainCat = MainCostCategory.TRIBUTOS;
                        } else {
                            mainCat = MainCostCategory.DESPESAS_ADM;
                        }
                    }
                    
                    const catDetails = COST_CATEGORIES.find(c => c.id === mainCat);
                    if(!catDetails) throw new Error(`Categoria principal para "${nameColumnValue}" não pôde ser determinada.`);
                    
                    entry.mainCategory = mainCat;
                    entry.subCategoryItemId = catDetails.subItems[0].id;
                    entry.subCategoryItemName = catDetails.subItems[0].name;
                    entry.month = getMonthYear(rowData.Data, rowData.Mes);
                    entry.description = nameColumnValue;

                    const amount = parseBrazilianNumber(rowData.ValorSomado);
                     if (isNaN(amount)) {
                        newWarnings.push(`Linha ${i + 2}: Valor monetário inválido: "${rowData.ValorSomado || 'vazio'}". A linha será ignorada.`);
                        continue;
                    }
                    entry.amount = amount;

                } else {
                    throw new Error("Tipo de arquivo CSV não reconhecido. Verifique os cabeçalhos.");
                }
                
                newProcessedData.push(entry);

            } catch (error: any) {
                newErrors.push(`Linha ${i + 2}: ${error.message}`);
            }
        }

        setProcessedData(newProcessedData);
        setErrors(newErrors);
        setWarnings(newWarnings);
        setStatus('preview');
    };

    const handleUpload = async () => {
        if (processedData.length === 0) return;
        setStatus('uploading');
        setUploadProgress(0);

        try {
          const result = await saveExpense(processedData);
          if(result.success) {
            setStatus('done');
          } else {
            setStatus('error');
            setErrors(prev => [...prev, `Erro no servidor: ${result.message}`]);
          }
        } catch (e: any) {
          setStatus('error');
          setErrors(prev => [...prev, `Erro de rede: ${e.message}`]);
        }
    };

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">Importação de Despesas</h1>
            
            <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    <div>
                        <h2 className='text-lg font-semibold mb-2'>1. Selecione o arquivo CSV</h2>
                        <Input 
                            id="csv-upload"
                            type="file" 
                            accept=".csv" 
                            onChange={handleFileChange} 
                            containerClassName="mb-0"
                        />
                         {file && <p className="text-xs text-gray-500 mt-1">Arquivo selecionado: {file.name}</p>}
                    </div>
                    <div>
                        <h2 className='text-lg font-semibold mb-2'>2. Processe e Envie</h2>
                        <p className='text-sm text-gray-600 dark:text-dark-text-secondary mb-3'>
                            Clique para processar o arquivo. Após a verificação, você poderá salvar os dados no sistema.
                        </p>
                        <div className='flex gap-4'>
                            <Button onClick={parseAndMapData} disabled={!file || status === 'parsing'}>
                                {status === 'parsing' ? 'Processando...' : 'Processar Arquivo'}
                            </Button>
                             <Button onClick={handleUpload} disabled={processedData.length === 0 || errors.length > 0 || status === 'uploading'}>
                                {status === 'uploading' ? `Salvando...` : 'Salvar no Banco de Dados'}
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>

            {(status === 'preview' || status === 'done' || status === 'error') && (
                <Card title="Resultados do Processamento">
                    <div className='space-y-4'>
                        <div className='flex gap-4 p-4 bg-gray-100 dark:bg-dark-surface rounded-lg'>
                            <div className='text-green-500'>
                                <p className='font-bold'>Entradas Válidas</p>
                                <p className='text-2xl'>{processedData.length}</p>
                            </div>
                             <div className='text-red-500'>
                                <p className='font-bold'>Erros Críticos</p>
                                <p className='text-2xl'>{errors.length}</p>
                            </div>
                             <div className='text-yellow-500'>
                                <p className='font-bold'>Alertas (Linhas Ignoradas)</p>
                                <p className='text-2xl'>{warnings.length}</p>
                            </div>
                        </div>

                        {errors.length > 0 && (
                            <div>
                                <h3 className='font-semibold mb-2 text-red-500'>Detalhes dos Erros (Importação Bloqueada):</h3>
                                <div className='p-3 bg-red-900/20 border border-red-500/30 rounded-md max-h-48 overflow-y-auto text-xs font-mono'>
                                    {errors.map((err, i) => <p key={i}>{err}</p>)}
                                </div>
                            </div>
                        )}

                        {warnings.length > 0 && (
                            <div>
                                <h3 className='font-semibold mb-2 text-yellow-500'>Detalhes dos Alertas (Linhas Ignoradas):</h3>
                                <div className='p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-md max-h-48 overflow-y-auto text-xs font-mono'>
                                    {warnings.map((warn, i) => <p key={i}>{warn}</p>)}
                                </div>
                            </div>
                        )}
                        
                        {processedData.length > 0 && (
                            <div>
                                <h3 className='font-semibold mb-2'>Pré-visualização (primeiras 10 linhas válidas)</h3>
                                <div className="overflow-x-auto border border-gray-200 dark:border-dark-tertiary rounded-lg">
                                    <table className="min-w-full text-xs">
                                        <thead className="bg-gray-50 dark:bg-dark-secondary">
                                            <tr>
                                                <th className="p-2 text-left">Obra</th>
                                                <th className="p-2 text-left">Categoria</th>
                                                <th className="p-2 text-left">Sub-item</th>
                                                <th className="p-2 text-right">Valor</th>
                                            </tr>
                                        </thead>
                                        <tbody className='divide-y divide-gray-200 dark:divide-dark-tertiary'>
                                            {processedData.slice(0, 10).map((item, i) => (
                                                <tr key={i} className="hover:bg-gray-100 dark:hover:bg-dark-surface">
                                                    <td className="p-2 truncate" title={projects.find(p=>p.id === item.projectId)?.name}>{projects.find(p=>p.id === item.projectId)?.name}</td>
                                                    <td className="p-2 truncate" title={item.mainCategory}>{item.mainCategory}</td>
                                                    <td className="p-2 truncate" title={item.subCategoryItemName}>{item.subCategoryItemName}</td>
                                                    <td className="p-2 text-right font-mono">{item.amount?.toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {status === 'done' && <p className='p-4 text-center font-bold text-green-500 bg-green-500/10 rounded-lg'>Importação concluída com sucesso!</p>}
                        {status === 'error' && <p className='p-4 text-center font-bold text-red-500 bg-red-500/10 rounded-lg'>A importação falhou. Verifique os erros acima.</p>}
                    </div>
                </Card>
            )}

        </div>
    );
};
